// Staff (docs/PHASE7-CONTRACT.md "Partner Hub"): the shop's team, add by email (owner only), remove
// (never the last owner, never yourself). The server enforces every guard; these mirror them so the
// screen can explain a disabled button before anyone taps it. Pure: no DOM, no Intl.

/** @typedef {import('@popcart/core/v1').StaffMember} StaffMember */
/** @typedef {import('@popcart/core/v1').Me} Me */
/** @typedef {'shop_staff' | 'shop_owner'} StaffRole */

export const ROLE_LABEL = /** @type {const} */ ({ shop_owner: 'Owner', shop_staff: 'Staff' });

/** @type {{ value: StaffRole; label: string; help: string }[]} */
export const ROLE_OPTIONS = [
  { value: 'shop_staff', label: 'Staff', help: 'Runs orders, stations, products and hours.' },
  { value: 'shop_owner', label: 'Owner', help: 'Everything staff can do, plus the team.' },
];

export const OWNER_ONLY = 'Only the shop owner can add or remove people.';
export const MAX_EMAIL = 254;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * May this person change the team? The shop's owner, or a platform admin (the API lets admins act on
 * any shop).
 * @param {Me | null | undefined} me
 * @param {string} shopId
 */
export function canManageTeam(me, shopId) {
  if (!me) return false;
  return me.roles.some((r) => r.role === 'platform_admin' || (r.role === 'shop_owner' && r.shopId === shopId));
}

/** Owners first, then by when they joined; you stay where you fall. @param {StaffMember[]} list */
export function sortStaff(list) {
  return list.slice().sort((a, b) => {
    if (a.role !== b.role) return a.role === 'shop_owner' ? -1 : 1;
    if (a.addedAt !== b.addedAt) return a.addedAt < b.addedAt ? -1 : 1;
    return a.userId < b.userId ? -1 : 1;
  });
}

/** The name to show: name, else the email, else a placeholder. @param {StaffMember} m */
export function displayName(m) {
  return (m.name && m.name.trim()) || m.email || 'Invited person';
}

/**
 * Why this person can't be removed (null = they can). Mirrors the API's 409 guards.
 * @param {StaffMember} m
 * @param {StaffMember[]} team
 */
export function removeBlock(m, team) {
  if (m.you) return 'You can’t remove yourself. Another owner can.';
  if (m.role === 'shop_owner' && team.filter((x) => x.role === 'shop_owner').length <= 1) return 'A shop always keeps at least one owner.';
  return null;
}

/** Trimmed, lower-case. @param {string} raw */
export function normalizeEmail(raw) {
  return String(raw ?? '').trim().toLowerCase();
}

/**
 * The add form's problem, or null.
 * @param {string} raw
 * @param {StaffMember[]} team
 */
export function emailProblem(raw, team) {
  const e = normalizeEmail(raw);
  if (!e) return 'Enter their email.';
  if (e.length > MAX_EMAIL || !EMAIL_RE.test(e)) return 'That email doesn’t look right. Check it and try again.';
  if (team.some((m) => (m.email ?? '').toLowerCase() === e)) return 'That person is already on this shop.';
  return null;
}

/**
 * The sentence for a failed add or remove. The server's own message wins (it is written for people:
 * "A shop always keeps at least one owner."); 403 is the owner-only rule.
 * @param {{ status?: number; code?: string; message?: string } | null | undefined} err
 * @param {'add' | 'remove'} action
 */
export function staffProblem(err, action) {
  if (err && err.status === 403) return OWNER_ONLY;
  if (err && err.status === 0) return 'Can’t reach PopCart. Check your connection.';
  if (err && (err.status === 409 || err.status === 400 || err.status === 404) && err.message && !/^Request failed \(\d+\)$/.test(err.message)) return err.message;
  if (err && err.status === 409) return action === 'add' ? 'That person is already on this shop.' : 'You can’t remove that person.';
  return action === 'add' ? 'Couldn’t add them. Try again.' : 'Couldn’t remove them. Try again.';
}

const MIN = 60_000;
const DAY = 24 * 60 * MIN;
/**
 * "Signed in today" / "Signed in 3 days ago" / "Hasn't signed in yet".
 * @param {string | null} lastLoginAt
 * @param {number} now
 */
export function lastSeen(lastLoginAt, now) {
  if (!lastLoginAt) return 'Hasn’t signed in yet';
  const t = Date.parse(lastLoginAt);
  if (!Number.isFinite(t)) return 'Hasn’t signed in yet';
  const days = Math.floor(Math.max(0, now - t) / DAY);
  if (days === 0) return 'Signed in today';
  if (days === 1) return 'Signed in yesterday';
  if (days < 60) return `Signed in ${days} days ago`;
  return `Signed in ${Math.floor(days / 30)} months ago`;
}

/** "Added 5 Oct 2026". @param {string} iso */
export function addedText(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const M = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `Added ${d.getDate()} ${M[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * The client calls the page makes (tests pass a fake api).
 * @param {Pick<import('@popcart/core/v1').PopCartApi, 'addShopStaff' | 'removeShopStaff'>} api
 * @param {string} shopId
 */
export function staffActions(api, shopId) {
  return {
    /** @param {string} email @param {StaffRole} role */
    add: (email, role) => api.addShopStaff(shopId, { email: normalizeEmail(email), role }),
    /** @param {string} userId */
    remove: (userId) => api.removeShopStaff(shopId, userId),
  };
}
